import React from 'react';

export interface SwitchProps {
  label?: string;
  checked?: boolean;
  onChange?: (checked: boolean) => void;
  disabled?: boolean;
  size?: 'sm' | 'md';
  className?: string;
}

const TRACK_SIZES = {
  sm: 'w-7 h-4',
  md: 'w-9 h-5',
} as const;

const THUMB_SIZES = {
  sm: 'w-3 h-3 translate-x-0.5',
  md: 'w-4 h-4 translate-x-0.5',
} as const;

const THUMB_TRANSLATE = {
  sm: 'translate-x-3',
  md: 'translate-x-4',
} as const;

const Switch = React.forwardRef<HTMLButtonElement, SwitchProps>(
  (
    {
      label,
      checked = false,
      onChange,
      disabled,
      size = 'md',
      className = '',
    },
    ref,
  ) => {
    const trackClasses = [
      'relative rounded-full transition-colors inline-flex shrink-0',
      TRACK_SIZES[size],
      checked ? 'bg-brand' : 'bg-surface-secondary border border-subtle',
    ]
      .filter(Boolean)
      .join(' ');

    const thumbClasses = [
      'absolute left-0 top-1/2 -translate-y-1/2 rounded-full bg-white transition-transform',
      THUMB_SIZES[size],
      checked ? THUMB_TRANSLATE[size] : 'translate-x-0.5',
    ]
      .filter(Boolean)
      .join(' ');

    const button = (
      <button
        ref={ref}
        type="button"
        role="switch"
        aria-checked={checked}
        disabled={disabled}
        onClick={() => onChange?.(!checked)}
        className={`${trackClasses} ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
      >
        <span className={thumbClasses} />
      </button>
    );

    const wrapperClasses = [
      'flex items-center gap-2',
      disabled && 'opacity-50 cursor-not-allowed',
      className,
    ]
      .filter(Boolean)
      .join(' ');

    if (label) {
      return (
        <div className={wrapperClasses}>
          {button}
          <span className="text-sm text-content-secondary">{label}</span>
        </div>
      );
    }

    return button;
  },
);

Switch.displayName = 'Switch';

export default Switch;
